import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';

import { BracelitEvent } from '@shared/models/bracelit/bracelit-event';
import { Voucher } from '@shared/models/bracelit/voucher';
import { Product } from '@shared/models/bracelit/product';
import { ApiService, AuthenticationService, SnackBarService } from '@shared/services';
import { WristbandsService } from '@shared/services/wristbands.service';
import { VoucherBlueprintService } from '@shared/services/voucher-blueprint.service';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';

@Component({
  selector: 'app-acquired-vouchers',
  templateUrl: './acquired-vouchers.component.html',
  styleUrls: ['./acquired-vouchers.component.scss'],
  standalone: false
})
export class AcquiredVouchersComponent implements OnInit {
  vouchers: Voucher[] = [];
  event: BracelitEvent;
  eventId: string;
  wristbandId: string;
  walletId: string;
  allVouchersBP: any[] = [];
  voucherLabelMinus: string;
  voucherLabelMayus: string;
  currentLang: string;
  loading = true;
  voucherDetails: Map<string, { voucherBlueprint: any; products: Product[]; loading: boolean; gastado: number }> = new Map();

  // Labels para los detalles
  leftLabel: string;
  leftLabel2: string;
  tokensLabel2: string;
  tokenLabel2: string;
  messageMoneyExtra: string;
  buyInterval: string;
  useInterval: string;
  maximumToSellText: string;
  maximumTimesToRechargeText: string;
  start: string;
  end: string;

  constructor(
    public dialogRef: MatDialogRef<AcquiredVouchersComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialog: MatDialog,
    private apiService: ApiService,
    private snackBar: SnackBarService,
    private authService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute,
    public wristbandsService: WristbandsService,
    private voucherBlueprintService: VoucherBlueprintService
  ) {
    this.dialogRef.disableClose = true;
    this.event = this.data.event;
    this.eventId = this.data.eventId;
    this.wristbandId = this.data.wristbandId;
    this.walletId = this.data.walletId;
    this.currentLang = localStorage.getItem('lang') === 'es' ? 'es' : 'en';
    this.voucherLabelMinus = (this.currentLang === 'es') ? 'bonos' : 'vouchers';
    this.voucherLabelMayus = (this.currentLang === 'es') ? 'BONOS' : 'VOUCHERS';

    // Inicializar labels
    this.leftLabel = (this.currentLang === 'es') ? 'Te quedan' : 'Left';
    this.leftLabel2 = (this.currentLang === 'es') ? 'Has gastado' : 'You have spent';
    this.tokensLabel2 = (this.currentLang === 'es') ? 'fichas' : 'tokens';
    this.tokenLabel2 = (this.currentLang === 'es') ? 'ficha' : 'token';
    this.messageMoneyExtra = (this.currentLang === 'es') ? 'Con este bono se recarga un extra de' : 'With this voucher an extra of';
    this.buyInterval = (this.currentLang === 'es') ? 'Intervalo de compra' : 'Buy interval';
    this.useInterval = (this.currentLang === 'es') ? 'Intervalo de uso' : 'Use interval';
    this.maximumToSellText = (this.currentLang === 'es') ? 'Unidades a la venta' : 'Units for sale';
    this.maximumTimesToRechargeText = (this.currentLang === 'es') ? 'Máximas unidades por persona' : 'Maximum units per person';
    this.start = (this.currentLang === 'es') ? 'Inicio' : 'Start';
    this.end = (this.currentLang === 'es') ? 'Fin' : 'End';
  }

  async ngOnInit(): Promise<void> {
    await this.loadVouchers();
  }

  async loadVouchers(): Promise<void> {
    try {
      this.loading = true;
      const accountId = this.wristbandsService.selectedWristband['accountId'];

      const [vouchers, vouchersBlueprint] = await Promise.all([
        this.voucherBlueprintService.fetchClientVouchers(this.eventId, this.wristbandId, this.walletId),
        this.voucherBlueprintService.fetchVoucherBlueprints(accountId, this.eventId, this.wristbandId)
      ]);

      await this.checkExpiration(vouchers, vouchersBlueprint);
    } catch (error) {
      console.error('Error loading vouchers:', error);
      this.snackBar.openSnackBar(
        (this.currentLang === 'es')
          ? 'Error al cargar los bonos. Por favor, inténtelo de nuevo.'
          : 'Error loading vouchers. Please try again.'
      );
      this.loading = false;
    }
  }

  async checkExpiration(vouchers, vouchersBlueprint) {
    if (!vouchers) {
      this.vouchers = [];
      this.loading = false;
      return;
    }

    const vouchersCheck = [];
    const today = new Date();
    this.allVouchersBP = [];

    if (this.wristbandsService.selectedWristband && vouchersBlueprint) {
      vouchersBlueprint.forEach(voucherBP => {
        if (!voucherBP.preSaleOffer || (new Date(voucherBP.preSaleOffer.replace(/-/g, "/")) > today && new Date(voucherBP.preSaleOfferStart.replace(/-/g, "/")) < today)) {
          this.allVouchersBP.push(voucherBP);
        }
      });

      // OPTIMIZACIÓN: Crear Map para lookup O(1) en lugar de loop O(n×m)
      const blueprintMap = new Map(vouchersBlueprint.map(bp => [bp.id, bp]));

      for (const voucher of vouchers) {
        const voucherBlueprint = blueprintMap.get((voucher as any).voucherBlueprintId) as any;
        if (voucherBlueprint) {
          if (voucherBlueprint.formData && voucher.info) {
            try {
              let parsedInfo: any;
              try {
                parsedInfo = typeof voucher.info === 'string' ? JSON.parse(voucher.info) : voucher.info;
              } catch (e) {
                console.error('Error parsing voucher.info:', e);
                parsedInfo = voucher.info;
              }

              let formData: any;
              try {
                formData = typeof voucherBlueprint.formData === 'string' 
                  ? JSON.parse(voucherBlueprint.formData) 
                  : voucherBlueprint.formData;
              } catch (e) {
                console.error('Error parsing voucherBlueprint.formData:', e);
                formData = voucherBlueprint.formData;
              }

              if (Array.isArray(formData) && parsedInfo && typeof parsedInfo === 'object') {
                let info: any = {};
                formData.forEach((vbElement: any) => {
                  // Buscar la clave en parsedInfo que coincida con vbElement.name
                  if (parsedInfo.hasOwnProperty(vbElement.name)) {
                    info[vbElement.webName] = parsedInfo[vbElement.name];
                  }
                });
                voucher.info = info;
              }
            } catch (error) {
              console.error('Error processing voucher info:', error);
            }
          }
          (voucher as any).endingDate = voucherBlueprint.endingDate;
          (voucher as any).preSaleOffer = voucherBlueprint.preSaleOffer;
          if (voucherBlueprint.expirationTime !== null) {
            const date = new Date(voucherBlueprint.expirationTime);
            if (date <= today) {
              vouchersCheck.push(voucher);
            }
          } else {
            vouchersCheck.push(voucher);
          }
        }
      }

      this.vouchers = vouchersCheck.sort((a, b) => {
        if (a.createdAt > b.createdAt) {
          return -1;
        } else {
          return 1;
        }
      });
      this.vouchers.forEach(element => {
        element.usesLeft = element.usesLeft / 1;
        element.uses = element.uses / 1;
      });
    } else if (!this.wristbandsService.selectedWristband) {
      this.router.navigate([`home`]);
    } else {
      // Caso cuando vouchersBlueprint es null pero selectedWristband existe
      this.vouchers = vouchers;
    }
    this.loading = false;
  }

  getVoucherInfoEntries(voucherInfo: any): Array<{key: string, value: any}> {
    if (!voucherInfo) {
      return [];
    }
    // Si ya es un array, devolverlo tal cual
    if (Array.isArray(voucherInfo)) {
      return voucherInfo;
    }
    // Si es un objeto, convertirlo a array de {key, value}
    return Object.keys(voucherInfo).map(key => ({
      key: key,
      value: voucherInfo[key]
    }));
  }

  onClose(): void {
    this.dialogRef.close({ reloadVouchers: false });
  }

  async loadVoucherDetails(voucher: Voucher): Promise<void> {
    if (this.voucherDetails.has(voucher.id)) {
      return; // Ya está cargado
    }

    // Marcar como cargando
    this.voucherDetails.set(voucher.id, {
      voucherBlueprint: null,
      products: [],
      loading: true,
      gastado: 0
    });

    try {
      // Buscar el voucherBlueprint en allVouchersBP
      let voucherBlueprint = this.allVouchersBP.find(bp => bp.id === voucher.voucherBlueprintId);

      // Si no está en allVouchersBP, buscarlo
      if (!voucherBlueprint) {
        const accountId = this.wristbandsService.selectedWristband['accountId'];
        const blueprints = await this.voucherBlueprintService.fetchVoucherBlueprints(accountId, this.eventId, this.wristbandId);
        voucherBlueprint = blueprints.find(bp => bp.id === voucher.voucherBlueprintId);
      }

      // Obtener productos
      const voucherBlueprintId = voucherBlueprint ? voucherBlueprint.id : voucher.voucherBlueprintId;
      const products = await this.voucherBlueprintService.fetchProducts(voucherBlueprintId);

      // Calcular gastado
      const gastado = voucherBlueprint && voucher ?
        (voucherBlueprint.uses - voucher.usesLeft >= 0 ? voucherBlueprint.uses - voucher.usesLeft : 0) : 0;

      // Actualizar detalles
      this.voucherDetails.set(voucher.id, {
        voucherBlueprint: voucherBlueprint,
        products: products || [],
        loading: false,
        gastado: gastado
      });
    } catch (error) {
      console.error('Error loading voucher details:', error);
      this.voucherDetails.set(voucher.id, {
        voucherBlueprint: null,
        products: [],
        loading: false,
        gastado: 0
      });
    }
  }

  getVoucherDetails(voucherId: string) {
    return this.voucherDetails.get(voucherId);
  }

  onDetailsToggle(detailsElement: HTMLDetailsElement, voucher: Voucher): void {
    // Usar setTimeout para que el estado 'open' se actualice después del click
    setTimeout(() => {
      if (detailsElement.open) {
        this.loadVoucherDetails(voucher);
      }
    }, 0);
  }

  cancelVoucher(voucher: any, i: number) {
    const dialogRef2 = this.dialog.open(ConfirmationDialogComponent, {
      width: '500px',
      minWidth: '320px',
      data: (this.currentLang === 'es') ? '¿Está seguro de que desea cancelar la reserva?' : 'Are you sure you want to cancel voucher?',
      autoFocus: false,
      restoreFocus: false
    });
    dialogRef2.afterClosed().subscribe(result => {
      if (result) {
        this.loading = true;
        let f = 0;
        const wristbands = JSON.parse(localStorage.getItem('wristbands'));
        for (let wrist of wristbands) {
          if (this.wristbandsService.selectedWristband['id'] === wrist.id) {
            this.apiService.delete(`accounts/${this.wristbandsService.selectedWristband['accountId']}/events/${this.eventId}/wristbands/${this.wristbandsService.selectedWristband['id']}/vouchers/${voucher}/cancelAccessVoucher`).subscribe({
              next: async (data) => {
                if (data.status === 200) {
                  this.wristbandsService.selectedWallet.money = Number(this.wristbandsService.selectedWallet.money) + Number((this.allVouchersBP.filter(v => v.id === this.vouchers[i].voucherBlueprintId))[0].price);
                  this.snackBar.openSnackBar('Reserva cancelada correctamente.');
                  // Recargar vouchers después de cancelar
                  await this.loadVouchers();
                  // Notificar al componente padre que debe recargar los vouchers
                  this.dialogRef.close({ reloadVouchers: true });
                }
              },
              error: (err) => {
                console.error(err);
                this.snackBar.openSnackBar('No fue posible cancelar la reserva. Por favor, inténtelo de nuevo.');
                this.loading = false;
              }
            });
          } else {
            f += 1;
          }
        }
        if (f === wristbands.length) {
          this.authService.logout('../qr-inicial');
        }
      }
    });
  }

  checkCanceledVoucher(voucher: any) {
    if (voucher.hasCancelation) {
      if (!voucher.preSaleOffer || (new Date(voucher.preSaleOffer.replace(/-/g, "/")) > new Date())) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  buildArray(n): any[] {
    return Array(parseInt(n, 10));
  }

  parseVoucherId(value: string) {
    return value.split('-')[0].toUpperCase();
  }
}

